/**
 * Luma's voice (2026-10-05). Quest Browser has no speech synthesis, so the server speaks for
 * her (Kokoro, `POST /api/assistant/speak`): the text is split into sentences, each fetched as
 * a WAV and played through a positional audio source at her head, the next fetched while one
 * plays. Lines she is about to say can be fetched ahead (`prefetch`), so the tour starts
 * talking at once. Where the server has no voice, the browser's synthesis is the fallback.
 */
import type * as THREE from 'three';
import { decodeCues, speechChunks, type Viseme, type VisemeCue } from '@clinspace/shared';
import { api } from '../lib/api.ts';
import { hostVoice, speak as speakInBrowser, speechAvailable, stopSpeaking } from './speech.ts';

/**
 * The server's audio by sentence, kept across scenes (2026-10-06): the welcome is fetched in
 * the lobby while the room code is typed, so Luma starts speaking as the room appears.
 */
const audioBytes = new Map<
  string,
  { bytes: Promise<SpeechBytes>; ahead: boolean; done: boolean }
>();

/** A sentence's audio as fetched, and its mouth shapes (the `x-clinspace-visemes` header). */
interface SpeechBytes {
  bytes: ArrayBuffer;
  cues: VisemeCue[];
}

/**
 * A sentence's audio, fetched once. `ahead` asks the server to make it after lines being said
 * (2026-10-06); a sentence fetched ahead that is needed now and has not arrived is asked for
 * again, which moves it up the server's queue.
 */
function speechBytes(sentence: string, ahead = false): Promise<SpeechBytes> {
  const held = audioBytes.get(sentence);
  if (held && (ahead || !held.ahead || held.done)) return held.bytes;
  {
    const p = fetch(api('assistant', 'speak'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text: sentence, ...(ahead ? { ahead: true } : {}) }),
    }).then(async (res) => {
      if (!res.ok) throw new Error(`speak ${res.status}`);
      let cues: VisemeCue[] = [];
      try {
        cues = decodeCues(JSON.parse(res.headers.get('x-clinspace-visemes') ?? '[]'));
      } catch {
        /* no lip sync for this sentence; the jaw follows the loudness instead */
      }
      return { bytes: await res.arrayBuffer(), cues };
    });
    const entry = { bytes: p, ahead, done: false };
    p.then(
      () => (entry.done = true),
      () => audioBytes.get(sentence) === entry && audioBytes.delete(sentence),
    );
    audioBytes.set(sentence, entry);
    if (audioBytes.size > 60) audioBytes.delete(audioBytes.keys().next().value!);
    return p;
  }
}

/**
 * Fetches the start of a line before Luma's room exists. Where the server has no voice the
 * request fails quietly and the room falls back to the browser's voice as before.
 */
export function warmSpeech(text: string): void {
  for (const s of speechChunks(text).slice(0, 2)) speechBytes(s, true).catch(() => undefined);
}

/** Lips move a little ahead of the sound they make. */
const LIP_LEAD = 0.03;

/** The mouth shape to show now, the next one, and how far to blend toward it (0..1). */
export interface MouthShape {
  now: Viseme;
  next: Viseme;
  toNext: number;
}

interface Sentence {
  buffer: AudioBuffer;
  cues: VisemeCue[];
}

export class HostVoice {
  /** Whether the server can speak (set from the assistant info). */
  serverVoice = false;
  private readonly fetched = new Map<string, Promise<Sentence | undefined>>();
  /**
   * True while a line has started but its next piece has not arrived (the server's voice was
   * slower than her speech): her talking pose pauses until it does (2026-10-06).
   */
  waiting = false;
  /** The playing sentence's mouth shapes, and when it started on the audio clock. */
  private cues: VisemeCue[] = [];
  private startedAt = 0;
  private token = 0;
  private source: AudioBufferSourceNode | undefined;
  /** Resolves the sentence playing now (also when it is cut off). */
  private endCurrent: (() => void) | undefined;
  private panner: PannerNode | undefined;
  private analyser: AnalyserNode | undefined;
  private readonly levels = new Uint8Array(256);
  /** Dev hook: how the last line was spoken. */
  lastRoute: 'server' | 'browser' | 'none' | undefined;
  lastError: string | undefined;

  constructor(
    private readonly context: () => AudioContext | undefined,
    private readonly position: () => THREE.Vector3,
  ) {}

  get available(): boolean {
    return this.serverVoice || speechAvailable();
  }

  /** Loudness of the voice now, 0..1 (drives the speaking ring). */
  level(): number {
    if (!this.analyser || !this.source) return 0;
    this.analyser.getByteTimeDomainData(this.levels);
    let sum = 0;
    for (const v of this.levels) sum += ((v - 128) / 128) ** 2;
    return Math.min(1, Math.sqrt(sum / this.levels.length) * 4);
  }

  /** Fetches the audio for lines that are about to be said. */
  prefetch(text: string): void {
    if (!this.serverVoice) return;
    for (const s of speechChunks(text).slice(0, 3)) void this.audioFor(s, true);
  }

  /**
   * Speaks the text; resolves when it has been said, or cut off by `stop` or a new line.
   * `onStart` runs when the voice begins (the server's audio can take a second or two to
   * arrive), so her gestures, mouth and caption start with the sound, not before it.
   */
  async speak(text: string, onStart?: () => void): Promise<void> {
    const token = ++this.token;
    this.stopSource();
    stopSpeaking();
    let started = false;
    const start = () => {
      if (started || token !== this.token) return;
      started = true;
      onStart?.();
    };
    const ctx = this.context();
    if (this.serverVoice && ctx) {
      this.lastRoute = 'server';
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
      // Short pieces, the first shortest (shared/speech.ts): she starts within about a second.
      const parts = speechChunks(text);
      // Ask for the first two at once; each later one while its predecessor plays.
      parts.slice(0, 2).forEach((p) => void this.audioFor(p));
      for (let i = 0; i < parts.length; i++) {
        if (token !== this.token) return;
        const next = parts[i + 1];
        if (next) void this.audioFor(next);
        // Between pieces, a piece not made yet is a pause: she stops talking until it comes.
        this.waiting = started;
        const sentence = await this.audioFor(parts[i]!);
        this.waiting = false;
        if (token !== this.token) return;
        // The first sentence is ready: she starts now (silently, if it failed to arrive).
        start();
        if (sentence) await this.play(ctx, sentence, token);
      }
      start();
      return;
    }
    if (speechAvailable()) {
      this.lastRoute = 'browser';
      await speakInBrowser(text, { voice: hostVoice(), onStart: start });
      start();
      return;
    }
    this.lastRoute = 'none';
    start();
  }

  stop(): void {
    this.token++;
    this.waiting = false;
    this.stopSource();
    stopSpeaking();
  }

  /**
   * Lip sync: the mouth shape for the moment being heard, from the playing sentence's cues on
   * the audio clock (less the output latency, plus a small lead). Undefined when nothing with
   * timing is playing (the browser's voice, or a server without it): the jaw then follows the
   * loudness.
   */
  mouthShape(): MouthShape | undefined {
    const ctx = this.context();
    const cues = this.cues;
    if (!ctx || !this.source || !cues.length) return undefined;
    const latency = (ctx as AudioContext & { outputLatency?: number }).outputLatency || 0;
    const t = ctx.currentTime - this.startedAt - (latency || ctx.baseLatency || 0) + LIP_LEAD;
    let i = 0;
    while (i + 1 < cues.length && cues[i + 1]!.t <= t) i++;
    const now = cues[i]!;
    const next = cues[i + 1];
    if (!next) return { now: now.v, next: now.v, toNext: 0 };
    // The second half of a sound already leans toward the next (coarticulation).
    const into = (t - now.t) / Math.max(0.001, next.t - now.t);
    return { now: now.v, next: next.v, toNext: Math.max(0, Math.min(1, (into - 0.5) * 2)) * 0.5 };
  }

  private audioFor(sentence: string, ahead = false): Promise<Sentence | undefined> {
    let p = this.fetched.get(sentence);
    // Fetched ahead and needed now: ask again so the server moves it up (it is cached there).
    if (p && !ahead) speechBytes(sentence).catch(() => undefined);
    if (!p) {
      p = (async () => {
        try {
          // A copy: decoding detaches the buffer, and the shared one may be played again.
          const speech = await speechBytes(sentence, ahead);
          const ctx = this.context();
          if (!ctx) return undefined;
          const buffer = await ctx.decodeAudioData(speech.bytes.slice(0));
          return { buffer, cues: speech.cues };
        } catch (err) {
          this.lastError = (err as Error).message;
          this.fetched.delete(sentence);
          return undefined;
        }
      })();
      this.fetched.set(sentence, p);
      if (this.fetched.size > 120) this.fetched.delete(this.fetched.keys().next().value!);
    }
    return p;
  }

  private play(ctx: AudioContext, sentence: Sentence, token: number): Promise<void> {
    const { buffer } = sentence;
    return new Promise((resolve) => {
      if (!this.panner) {
        // A voice in the room: HRTF from where Luma stands, falling off gently enough that the
        // whole room hears her (one host per room since 2026-10-06): about half her loudness at
        // 8 m and a third across the hall, where a person's voice would be a sixth.
        this.panner = ctx.createPanner();
        this.panner.panningModel = 'HRTF';
        this.panner.distanceModel = 'inverse';
        this.panner.refDistance = 3;
        this.panner.rolloffFactor = 0.5;
        this.analyser = ctx.createAnalyser();
        this.analyser.fftSize = 256;
        this.panner.connect(this.analyser);
        this.analyser.connect(ctx.destination);
      }
      const p = this.position();
      this.panner.positionX.value = p.x;
      this.panner.positionY.value = p.y;
      this.panner.positionZ.value = p.z;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.value = 1.4;
      source.connect(gain).connect(this.panner);
      this.source = source;
      this.endCurrent = resolve;
      source.onended = () => {
        if (this.source === source) {
          this.source = undefined;
          this.cues = [];
        }
        this.endCurrent = undefined;
        resolve();
      };
      if (token !== this.token) return resolve();
      this.cues = sentence.cues;
      this.startedAt = ctx.currentTime;
      source.start();
    });
  }

  /** Keeps the voice at Luma as she moves (the tour). */
  update(): void {
    if (!this.panner) return;
    const p = this.position();
    this.panner.positionX.value = p.x;
    this.panner.positionY.value = p.y;
    this.panner.positionZ.value = p.z;
  }

  private stopSource(): void {
    const s = this.source;
    this.source = undefined;
    this.cues = [];
    const end = this.endCurrent;
    this.endCurrent = undefined;
    end?.();
    if (s) {
      s.onended = null;
      try {
        s.stop();
      } catch {
        // Already stopped.
      }
    }
  }
}
